<template>
  <v-container class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-exchange-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Traspasos entre Bodegas</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Historial de movimientos de stock de una bodega a otra</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
          color="success"
          class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm"
          @click="$router.push({ name: 'CreateStoreTransfer' })"
        >
          <v-icon left small>fas fa-plus</v-icon> Nuevo traspaso
        </v-btn>
    </v-row>

    <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Creada desde</div>
          <v-text-field
            v-model="filter.created_after"
            type="date"
            outlined dense hide-details
            class="modern-input"
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Creada hasta</div>
          <v-text-field
            v-model="filter.created_before"
            type="date"
            outlined dense hide-details
            class="modern-input"
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Bodega origen</div>
          <v-select
            v-model="filter.from_store"
            :items="stores"
            item-text="name"
            item-value="id"
            placeholder="Todas"
            outlined dense hide-details
            class="modern-input"
            clearable
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
            prepend-inner-icon="fas fa-warehouse"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Bodega destino</div>
          <v-select
            v-model="filter.to_store"
            :items="stores"
            item-text="name"
            item-value="id"
            placeholder="Todas"
            outlined dense hide-details
            class="modern-input"
            clearable
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
            prepend-inner-icon="fas fa-warehouse"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-card outlined class="modern-card shadow-sm overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="transfers"
        :options.sync="options"
        :server-items-length="parseInt(totalItems)"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        sort-by="created_at"
        sort-desc
        show-expand
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Registros encontrados: {{totalItems}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              prepend-inner-icon="fas fa-search"
              placeholder="Buscar por artículo, bodega o referencia..."
              outlined dense hide-details
              class="modern-search-field"
              @keyup="searchTimeOut"
              :disabled="loading"
            />
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="caption font-weight-bold grey--text">{{item.created_at | moment('DD/MM/YYYY HH:mm')}}</div>
        </template>

        <template v-slot:item.id="{ item }">
          <v-chip x-small label class="font-weight-black px-2">#{{item.id}}</v-chip>
        </template>

        <template v-slot:item.route="{ item }">
          <div class="d-flex align-center">
            <span class="caption font-weight-black">{{item.from_store_name}}</span>
            <v-icon x-small color="primary" class="mx-2">fas fa-arrow-right</v-icon>
            <span class="caption font-weight-black">{{item.to_store_name}}</span>
          </div>
        </template>

        <template v-slot:item.items_count="{ item }">
          <span class="caption font-weight-black primary--text">{{item.items_count}}</span>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <v-simple-table dense class="bg-lightgrey">
              <tbody>
                <tr v-for="line in item.details" :key="line.article">
                  <td class="caption font-weight-black primary--text" width="140">[{{line.article_sku}}]</td>
                  <td class="caption">{{line.article_name}}</td>
                  <td class="caption font-weight-black text-end" width="120">{{line.qty | number('0,0.00')}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fetchStoreTransfers, fetchStores } from '@/api/stocks'
import moment from 'moment'

export default {
  name: 'StoreTransfers',
  data() {
    return {
      loading: false,
      stores: [],
      transfers: [],
      totalItems: 0,
      search: '',
      timer: '',
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before: '',
        from_store: '',
        to_store: '',
      },
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      headers: [
        { text: 'Creada', value: 'created_at', class: 'table-header-2', sortable: false },
        { text: 'Nro.', value: 'id', class: 'table-header-2', sortable: false },
        { text: 'Usuario', value: 'created_by_full_name', class: 'table-header-2', sortable: false },
        { text: 'Ruta', value: 'route', class: 'table-header-2', sortable: false },
        { text: 'Ítems', value: 'items_count', class: 'table-header-2', sortable: false, align: 'center' },
        { text: 'Referencia', value: 'notes', class: 'table-header-2', sortable: false },
        { text: '', value: 'data-table-expand', sortable: false },
      ],
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      if (!this.filter.created_after) this.filter.created_after = ''
      if (!this.filter.created_before) this.filter.created_before = ''
      if (!this.filter.from_store) this.filter.from_store = ''
      if (!this.filter.to_store) this.filter.to_store = ''
      const { page, itemsPerPage } = this.options
      const query = `ordering=-created_at&page=${page}&page_size=${itemsPerPage}&from_store=${this.filter.from_store}&to_store=${this.filter.to_store}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}&search=${this.search}`
      try {
        const res = await fetchStoreTransfers(query)
        this.totalItems = res.count
        this.transfers = res.results
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 1000)
    },
  },
  async mounted() {
    this.stores = await fetchStores()
    this.fetch()
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }

.modern-search-field { width: 320px; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; background-color: white !important; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 52px !important; text-transform: uppercase; font-size: 0.65rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }

.v-avatar.border-white-sm { border: 2px solid white !important; }
</style>
